// Status palette for fleet health. Each status has:
//   main  — rails, icons, bars (non-text; needs 3:1)
//   bg    — the tint behind a status label or an active tile
//   text  — a label set on `bg` (needs 4.5:1)
// Red is kept for platform down (R1) alone, so it is never lost among
// others; core unstable is amber (a core service is failing), a scan backlog
// and not reporting are neutral (scanning is behind, nothing is down).
// Each is told apart by icon and label too. Measured on the light palette (WCAG 2.1):
//   r1     main #E5484D on white 3.91 (rails, icons) -> text #C93A3F on bg 4.59
//   r2     amber: text #B45309 on bg 4.84, main #F59E0B (icons, rails)
//   r3     slate: text #475569 on bg 6.92, main #64748B on white 4.76
//   stale  main #64748B on bg 4.34  -> text #475569 6.92
//   ok     main #15803D on bg 4.79  -> text = main
//   accent main #2563EB on bg 4.75  -> text #1D4ED8 6.16
// Every status colour is shown with an icon and a label, never alone.
export const statusPalette = {
  light: {
    r1: { main: '#E5484D', bg: '#FFF1F1', text: '#C93A3F' },
    r2: { main: '#F59E0B', bg: '#FFFBEB', text: '#B45309' },
    r3: { main: '#64748B', bg: '#F1F5F9', text: '#475569' },
    stale: { main: '#64748B', bg: '#F1F5F9', text: '#475569' },
    ok: { main: '#15803D', bg: '#F0FDF4', text: '#15803D' },
    accent: { main: '#2563EB', bg: '#EFF6FF', text: '#1D4ED8' },
  },
  dark: {
    r1: { main: '#F87171', bg: 'rgba(220, 38, 38, 0.16)', text: '#FCA5A5' },
    r2: { main: '#FBBF24', bg: 'rgba(245, 158, 11, 0.16)', text: '#FCD34D' },
    r3: { main: '#94A3B8', bg: 'rgba(100, 116, 139, 0.2)', text: '#CBD5E1' },
    stale: { main: '#94A3B8', bg: 'rgba(100, 116, 139, 0.2)', text: '#CBD5E1' },
    ok: { main: '#4ADE80', bg: 'rgba(21, 128, 61, 0.2)', text: '#86EFAC' },
    accent: { main: '#60A5FA', bg: 'rgba(37, 99, 235, 0.18)', text: '#93C5FD' },
  },
};

// The page runs in light mode; the dark set is defined for the theme.
export const status = statusPalette.light;

// Data hues: four families and a neutral, one recipe each, so the page stays
// calm and the set reads as one palette.
//   dot    — glyphs, bars, legend swatches (non-text)
//   text   — words in the hue, on white or on bg (4.5:1 or better)
//   bg     — the tint behind a chip
//   border — the chip's hairline
//   solid  — a filled chip, for the one value in a column that must stand out
// Red is not here: it belongs to platform down alone. What each family means:
//   blue    versions: the newest release strong, the one before it soft
//   green   healthy, recovered
//   amber   needs a look: core unstable, critical scans, credentials, got worse, just went quiet
//   violet  application bugs
//   ink     connectivity (a darker neutral)
//   slate   everything else: older releases, datastore, unclassified, long silent
export const hues = {
  green: { dot: '#22C55E', text: '#15803D', bg: '#F0FDF4', border: '#BBF7D0' },
  amber: { dot: '#F59E0B', text: '#B45309', bg: '#FFFBEB', border: '#FDE68A', solid: '#B45309' },
  violet: { dot: '#8B5CF6', text: '#6D28D9', bg: '#F5F3FF', border: '#DDD6FE' },
  // newest release: filled; the one before it: outlined, so the two read apart
  blue: { dot: '#2563EB', text: '#1E40AF', bg: '#DBEAFE', border: '#93C5FD' },
  blueSoft: { dot: '#93C5FD', text: '#2563EB', bg: '#FFFFFF', border: '#BFDBFE' },
  ink: { dot: '#64748B', text: '#334155', bg: '#F1F5F9', border: '#CBD5E1' },
  slate: { dot: '#94A3B8', text: '#475569', bg: '#F8FAFC', border: '#E2E8F0' },
};

// Version by rank against the newest release in the fleet, so when 4.3
// ships it takes the strong blue and 4.2 the soft one, with no code change.
export const versionHue = { current: hues.blue, previous: hues.blueSoft, older: hues.slate };

// What changed since yesterday: worse, better, or gone quiet.
export const changeHue = { worse: hues.amber, new: hues.amber, better: hues.green, recovered: hues.green, silent: hues.slate };

// Motion: one easing, three durations, all off under prefers-reduced-motion.
export const motion = {
  ease: 'cubic-bezier(0.2, 0, 0, 1)',
  hover: 120,
  expand: 200,
  drawer: 260,
};
